/**
 * Lazy-loaded Supabase client for community photos, comments and auth.
 *
 * `supabase` starts null and gets populated by the first `loadSupabase()`
 * call (typically from useAuth on App mount), which dynamic-imports the
 * 46 kB SDK chunk. Every consumer guards with `if (!supabase) return`,
 * and `export let` keeps the binding live so importers see the upgrade.
 *
 * URL + publishable key come from VITE_SUPABASE_* env vars with hard
 * fallbacks to the prod project. The publishable key is safe to ship —
 * Row Level Security in Supabase enforces real access control.
 */
import type { SupabaseClient } from "@supabase/supabase-js";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string || "https://bzaapmotlajmefgdkfnh.supabase.co";
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string || "sb_publishable_U-Jz4n7vvhgvmqUok5CAYg_7g06Sohv";

export let supabase: SupabaseClient | null = null;
let _loadPromise: Promise<SupabaseClient> | null = null;

export function loadSupabase(): Promise<SupabaseClient> {
  if (supabase) return Promise.resolve(supabase);
  if (_loadPromise) return _loadPromise;
  // Cachen ryddes hvis hentningen fejler. Uden det blev den afviste promise
  // liggende, og hvert senere forsoeg afviste oejeblikkeligt med den samme
  // fejl — uden at naa koden her.
  //
  // Det goer ikke et nyt forsoeg muligt, og det skal man vide foer man tror
  // det. Maalt i Chrome 21/9-2026: naar hentningen af et modul fejler,
  // bliver fejlen liggende i browserens modulkort, og et nyt import() af
  // samme specifier afviser med det samme uden at roere nettet. Kun en
  // genindlaesning af siden hjaelper, og det er derfor auth.unreachable
  // siger netop det. Rydningen her er for de fejl der sker efter
  // hentningen — createClient der kaster — hvor et nyt forsoeg virker.
  _loadPromise = import("@supabase/supabase-js").then(({ createClient }) => {
    supabase = createClient(supabaseUrl, supabaseAnonKey);
    return supabase;
  }).catch((err) => {
    _loadPromise = null;
    throw err;
  });
  return _loadPromise;
}

export const isSupabaseConfigured = () => true;
